import React, { useState, useMemo } from 'react';
import { X, Wand2, Plus, Trash2, Save, AlertCircle, CheckCircle, Loader2, Pencil } from 'lucide-react';
import SessionEditModal from './SessionEditModal';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';

/**
 * Auto-groupe les games par tranches temporelles cohérentes
 * Une "session" = des games avec moins de 4h d'écart entre elles
 */
function autoGroupSessions(games, maxGapHours = 4) {
  const sortedGames = [...games].sort((a, b) => {
    const ta = new Date(a.timestamp || 0).getTime();
    const tb = new Date(b.timestamp || 0).getTime();
    return ta - tb;
  });

  if (sortedGames.length === 0) return [];

  const sessions = [];
  let currentSession = [sortedGames[0]];

  for (let i = 1; i < sortedGames.length; i++) {
    const prev = sortedGames[i - 1];
    const curr = sortedGames[i];
    const tPrev = new Date(prev.timestamp).getTime();
    const tCurr = new Date(curr.timestamp).getTime();
    const gapHours = (tCurr - tPrev) / (1000 * 60 * 60);

    if (gapHours > maxGapHours) {
      // Nouvelle session
      sessions.push(currentSession);
      currentSession = [curr];
    } else {
      currentSession.push(curr);
    }
  }
  sessions.push(currentSession);

  return sessions;
}

/**
 * Déduit le mood d'une session à partir des moods de ses games
 * Stratégie : mood le plus fréquent, avec priorité au négatif en cas d'égalité
 *   Tilté > Fatigué > Focus > Neutre
 */
const MOOD_PRIORITY = { 'Tilté': 4, 'Fatigué': 3, 'Focus': 2, 'Neutre': 1 };

export function deduceSessionMood(games) {
  if (!games || games.length === 0) return 'Neutre';

  const counts = {};
  games.forEach(g => {
    const mood = g.mental || 'Neutre';
    counts[mood] = (counts[mood] || 0) + 1;
  });

  // Trouver la fréquence max
  const maxCount = Math.max(...Object.values(counts));

  // Tous les moods qui ont la fréquence max
  const candidates = Object.entries(counts)
    .filter(([_, c]) => c === maxCount)
    .map(([mood]) => mood);

  // En cas d'égalité, on prend celui de plus haute priorité (négatif)
  candidates.sort((a, b) => (MOOD_PRIORITY[b] || 0) - (MOOD_PRIORITY[a] || 0));

  return candidates[0];
}

/**
 * Génère un insight automatique basé sur la composition des moods
 */
export function generateAutoInsight(games, dominantMood) {
  if (!games || games.length === 0) return null;

  const counts = {};
  games.forEach(g => {
    const mood = g.mental || 'Neutre';
    counts[mood] = (counts[mood] || 0) + 1;
  });

  const breakdown = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([mood, c]) => `${c} ${mood}`)
    .join(', ');

  return `Session auto-groupée · Mood dominant : ${dominantMood} · Détail : ${breakdown}`;
}

export default function SessionsManagerModal({ games, sessions, onClose, onAutoGroup, onDeleteSession, onUpdateSession }) {
  const [mode, setMode] = useState('view'); // 'view' | 'auto' | 'preview'
  const [maxGapHours, setMaxGapHours] = useState(4);
  const [previewSessions, setPreviewSessions] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingSession, setEditingSession] = useState(null);

  // Games qui ne sont pas encore dans une session (= games après la dernière session fermée)
  const unsessionedGames = useMemo(() => {
    const allGameIdsInSessions = new Set(sessions.flatMap(s => s.game_ids || []));
    return games.filter(g => !allGameIdsInSessions.has(g.id));
  }, [games, sessions]);

  const computePreview = () => {
    const groups = autoGroupSessions(unsessionedGames, maxGapHours);
    setPreviewSessions(groups);
    setMode('preview');
  };

  const applyAutoGroup = async () => {
    if (!previewSessions) return;
    setProcessing(true);
    try {
      // Créer une session pour chaque groupe (sauf si 1 game, on peut skip ou pas)
      for (const group of previewSessions) {
        const wins = group.filter(g => g.result === 'W').length;
        const losses = group.length - wins;
        const lastGame = group[group.length - 1];
        const closedAt = new Date(lastGame.timestamp);
        // Décaler de quelques minutes pour que la session soit "après" la dernière game
        closedAt.setMinutes(closedAt.getMinutes() + 5);

        // V5.4 : auto-déduction du mood depuis les games
        const dominantMood = deduceSessionMood(group);
        const autoInsight = generateAutoInsight(group, dominantMood);

        await onAutoGroup({
          closed_at: closedAt.toISOString(),
          games_count: group.length,
          wins,
          losses,
          game_ids: group.map(g => g.id),
          mental_global: dominantMood,
          insights: autoInsight
        });
      }
      onClose();
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal open onClose={onClose}
      title={`${sessions.length} sessions · ${unsessionedGames.length} games orphelines`}
      subtitle="Gestion des Sessions"
      size="xl">
        <div className="space-y-5">
          {mode === 'view' && (
            <>
              {/* Auto-grouping CTA */}
              {unsessionedGames.length > 0 && (
                <div className="border border-cyan-400/30 bg-cyan-400/5 p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Wand2 size={16} className="text-cyan-400" />
                    <div className="font-bold text-cyan-400">Auto-Grouping</div>
                  </div>
                  <div className="text-sm text-zinc-300 mb-3">
                    Tu as <strong>{unsessionedGames.length} games</strong> qui ne sont dans aucune session. Je peux les regrouper automatiquement en sessions cohérentes selon le temps écoulé entre les games.
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <label className="text-[10px] text-zinc-400 font-mono tracking-wider uppercase">Écart max</label>
                    <select
                      value={maxGapHours}
                      onChange={(e) => setMaxGapHours(+e.target.value)}
                      className="bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
                    >
                      <option value="2">2h (très serré)</option>
                      <option value="4">4h (recommandé)</option>
                      <option value="6">6h (large)</option>
                      <option value="12">12h (par jour)</option>
                      <option value="24">24h (par jour)</option>
                    </select>
                    <button
                      onClick={computePreview}
                      className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black px-4 py-2 font-black tracking-wider hover:opacity-90 transition text-xs flex items-center gap-2"
                    >
                      <Wand2 size={12} /> PRÉVISUALISER
                    </button>
                  </div>
                </div>
              )}

              {/* Liste des sessions existantes */}
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold mb-3">Sessions existantes ({sessions.length})</div>
                {sessions.length === 0 ? (
                  <div className="border border-dashed border-zinc-800 p-6 text-center text-zinc-500 text-sm">
                    Aucune session encore. Utilise l'auto-grouping ou ferme manuellement des sessions.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {sessions.map(s => {
                      const isPositive = s.wins > s.losses;
                      const moodColor = s.mental_global === 'Tilté' ? 'text-rose-400' : s.mental_global === 'Fatigué' ? 'text-amber-400' : s.mental_global === 'Focus' ? 'text-cyan-400' : 'text-zinc-400';
                      return (
                        <div key={s.id} className={`border p-3 flex items-center gap-3 ${isPositive ? 'border-cyan-400/20 bg-cyan-400/[0.02]' : 'border-fuchsia-500/20 bg-fuchsia-500/[0.02]'}`}>
                          <div>
                            <div className="text-xs text-zinc-400 font-mono">{new Date(s.closed_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                            <div className="flex items-center gap-2">
                              <div className={`font-black text-xl ${isPositive ? 'text-cyan-400' : 'text-fuchsia-400'}`}>{s.wins}-{s.losses}</div>
                              <div className={`text-[10px] tracking-wider uppercase font-bold ${moodColor}`}>· {s.mental_global || 'Neutre'}</div>
                            </div>
                          </div>
                          <div className="flex-1 text-xs text-zinc-500 font-mono min-w-0">
                            <div>{s.games_count} game{s.games_count > 1 ? 's' : ''}</div>
                            {s.insights && <div className="italic mt-1 truncate">{s.insights}</div>}
                          </div>
                          {confirmDelete === s.id ? (
                            <div className="flex items-center gap-2">
                              <button onClick={() => { onDeleteSession(s.id); setConfirmDelete(null); }} className="bg-rose-500 text-white px-2 py-1 text-xs font-bold">SUPPRIMER</button>
                              <button onClick={() => setConfirmDelete(null)} className="text-zinc-500 hover:text-white text-xs">Annuler</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              {onUpdateSession && (
                                <button
                                  onClick={() => setEditingSession(s)}
                                  className="text-zinc-500 hover:text-cyan-400 p-1.5 border border-zinc-800 hover:border-cyan-400/50 transition"
                                  title="Éditer mood + insights"
                                >
                                  <Pencil size={12} />
                                </button>
                              )}
                              <button
                                onClick={() => setConfirmDelete(s.id)}
                                className="text-zinc-500 hover:text-rose-400 p-1.5 border border-zinc-800 hover:border-rose-400/50 transition"
                                title="Supprimer"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {mode === 'preview' && previewSessions && (
            <>
              <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 flex items-start gap-3">
                <CheckCircle size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-zinc-300">
                  <strong className="text-cyan-400">{previewSessions.length} session{previewSessions.length > 1 ? 's' : ''} détectée{previewSessions.length > 1 ? 's' : ''}</strong> avec un écart max de {maxGapHours}h. Vérifie le résultat avant d'appliquer.
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {previewSessions.map((group, idx) => {
                  const wins = group.filter(g => g.result === 'W').length;
                  const losses = group.length - wins;
                  const isPositive = wins > losses;
                  const firstGame = group[0];
                  const lastGame = group[group.length - 1];
                  const startTime = new Date(firstGame.timestamp);
                  const endTime = new Date(lastGame.timestamp);
                  const durationHours = (endTime - startTime) / (1000 * 60 * 60);
                  const previewMood = deduceSessionMood(group);
                  const moodColor = previewMood === 'Tilté' ? 'text-rose-400' : previewMood === 'Fatigué' ? 'text-amber-400' : previewMood === 'Focus' ? 'text-cyan-400' : 'text-zinc-400';

                  return (
                    <div key={idx} className={`border p-3 ${isPositive ? 'border-cyan-400/30 bg-cyan-400/5' : 'border-fuchsia-500/30 bg-fuchsia-500/5'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-semibold">Session {idx + 1}</div>
                          <div className="flex items-center gap-3">
                            <div className={`font-black text-2xl ${isPositive ? 'text-cyan-400' : 'text-fuchsia-400'}`}>{wins}-{losses}</div>
                            <div className={`text-[10px] tracking-wider uppercase font-bold ${moodColor}`}>· {previewMood}</div>
                          </div>
                        </div>
                        <div className="text-right text-[10px] text-zinc-400 font-mono">
                          <div>{startTime.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                          <div>→ {endTime.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                          <div className="text-zinc-500">Durée : {durationHours.toFixed(1)}h</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {group.map(g => (
                          <span key={g.id} className={`text-[9px] font-mono px-1.5 py-0.5 border ${g.result === 'W' ? 'border-cyan-400/30 text-cyan-400' : 'border-fuchsia-400/30 text-fuchsia-400'}`}>
                            {g.result} {g.champion}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 pt-3 border-t border-zinc-800">
                <button
                  onClick={() => { setMode('view'); setPreviewSessions(null); }}
                  disabled={processing}
                  className="flex-1 border border-zinc-800 text-zinc-400 p-3 hover:border-zinc-700 transition font-semibold tracking-wider text-sm disabled:opacity-50"
                >
                  ← MODIFIER
                </button>
                <button
                  onClick={applyAutoGroup}
                  disabled={processing}
                  className="flex-[2] bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black p-3 hover:opacity-90 transition font-black tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? <><Loader2 size={14} className="animate-spin" /> CRÉATION...</> : <><CheckCircle size={14} /> CRÉER LES {previewSessions.length} SESSIONS</>}
                </button>
              </div>
            </>
          )}
        </div>

      {editingSession && onUpdateSession && (
        <SessionEditModal
          session={editingSession}
          onClose={() => setEditingSession(null)}
          onSave={onUpdateSession}
        />
      )}
    </Modal>
  );
}
